import { IExpenseReport } from '../pages/Dashboard/vehicles_management/Expenses';
import { formatReportDate } from './formatReportDate';

interface BuildSignatureEmailParams {
  report: IExpenseReport;
  senderName?: string;
}

interface SignatureEmailData {
  signatureUrl: string;
  directorEmail: string;
  directorName: string;
  subject: string;
  message: string;
}

/**
 * Monta os dados do email de assinatura (URL, assunto, mensagem).
 * O envio é feito pelo backend via expenseReportsService.sendSignatureEmail().
 */
export const buildSignatureEmailData = ({
  report,
  senderName = 'Sistema SAMASC',
}: BuildSignatureEmailParams): SignatureEmailData => {
  const directorEmail = report.user?.email || '';
  const directorName =
    report.user?.name || report.director_name || 'Usuário Responsável';

  if (!directorEmail) {
    throw new Error(
      'Este usuário não possui email cadastrado. Atualize o cadastro em Administrador > Usuários.',
    );
  }

  // URL pública de assinatura
  const baseUrl = window.location.origin;
  const signatureUrl = `${baseUrl}/sign-expense/${report.id}`;

  // Calcular total e contar itens
  const totalItems = report.items.length;
  const totalValue = report.items.reduce((sum, item) => {
    if (item.expense_type === 'Quilometragem adicional') {
      return sum + (Number(item.additional_km) || 0) * 1.63;
    }
    return sum + (Number(item.amount) || 0);
  }, 0);

  const formattedValue = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(totalValue);

  const reportDate = formatReportDate(report.date, 'dd/MM/yyyy');

  const subject =
    'Solicitação de Assinatura Digital - Relatório de Despesas e Reembolsos';

  const message = `Prezado(a) ${directorName},

Você recebeu um novo relatório de despesas e reembolsos para assinatura digital.

INFORMAÇÕES DO RELATÓRIO:
• Data do Relatório: ${reportDate}
• Setor: ${report.sector}
• Seção: ${report.section}
• Centro de Custo: ${report.cost_center}
• Total de Itens: ${totalItems}
• Valor Total: ${formattedValue}

Para visualizar e assinar digitalmente este relatório, acesse o link pessoal abaixo.

A assinatura será realizada através da plataforma gov.br com certificado digital ICP-Brasil.

Atenciosamente,
${senderName}
COOASGO - Cooperativa de Trabalho`;

  return {
    signatureUrl,
    directorEmail,
    directorName,
    subject,
    message,
  };
};

// Função para obter email a partir de um relatório
export const getDirectorEmail = (report: IExpenseReport): string | null => {
  return report.user?.email || null;
};
